import {
  fetchMemberProfile,
  isRefreshTokenRejected,
  refreshMemberToken,
  type MemberLoginResult,
  type MemberProfile,
} from "@/api/member";
import { persistNativeMemberSession } from "@/utils/nativeApp";

let memberRefreshPromise: Promise<string | null> | null = null;

const STORAGE_KEY = "storybox_user";
const REFERRAL_KEY = "storybox_ref_code";

export interface MemberSession {
  isLoggedIn: boolean;
  id: number;
  name: string;
  email: string;
  avatar: string;
  shareCode: string;
  shareUrl?: string;
  isDealer: boolean;
  pointsBalance: number;
  accessToken: string;
  accessTokenExpires: number;
  refreshToken: string;
  refreshTokenExpires: number;
}

const memberLoginReturnKey = "mfantasy_member_login_return";
const memberLoginFallbackPath = "/mylist";

function isMemberAuthPath(path: string): boolean {
  const pathname = path.split(/[?#]/, 1)[0].replace(/\/+$/, "") || "/";
  return [
    "/login",
    "/register",
    "/signup",
    "/forgot",
    "/forgot-password",
    "/password/reset",
  ].includes(pathname.toLowerCase());
}

export function rememberMemberLoginReturn(path: string) {
  if (typeof window === "undefined" || !path.startsWith("/") || path.startsWith("//") || isMemberAuthPath(path)) return;
  try {
    window.sessionStorage.setItem(memberLoginReturnKey, path);
  } catch {
    // Some embedded browsers may disable session storage.
  }
}

export function consumeMemberLoginReturn() {
  if (typeof window === "undefined") return memberLoginFallbackPath;
  try {
    const path = window.sessionStorage.getItem(memberLoginReturnKey) || memberLoginFallbackPath;
    window.sessionStorage.removeItem(memberLoginReturnKey);
    return path.startsWith("/") && !path.startsWith("//") && !isMemberAuthPath(path) ? path : memberLoginFallbackPath;
  } catch {
    return memberLoginFallbackPath;
  }
}

export const guestSession: MemberSession = {
  isLoggedIn: false,
  id: 0,
  name: "guest",
  email: "",
  avatar: "",
  shareCode: "",
  isDealer: false,
  pointsBalance: 0,
  accessToken: "",
  accessTokenExpires: 0,
  refreshToken: "",
  refreshTokenExpires: 0,
};

function profileName(profile: MemberProfile) {
  return profile.nickName || profile.userName || profile.email || `会员${profile.id}`;
}

export function buildSessionFromLogin(result: MemberLoginResult): MemberSession {
  return {
    isLoggedIn: true,
    id: result.user.id,
    name: profileName(result.user),
    email: result.user.email || "",
    avatar: result.user.avatar || "",
    shareCode: result.user.shareCode || "",
    isDealer: Boolean(result.user.isDealer),
    pointsBalance: Number(result.user.pointsBalance) || 0,
    accessToken: result.accessToken || "",
    accessTokenExpires: Number(result.accessTokenExpires) || 0,
    refreshToken: result.refreshToken || "",
    refreshTokenExpires: Number(result.refreshTokenExpires) || 0,
  };
}

export function buildSessionFromProfile(profile: MemberProfile, current: MemberSession): MemberSession {
  return {
    ...current,
    isLoggedIn: true,
    id: profile.id,
    name: profileName(profile),
    email: profile.email || "",
    avatar: profile.avatar || "",
    shareCode: profile.shareCode || "",
    isDealer: Boolean(profile.isDealer),
    pointsBalance: Number(profile.pointsBalance) || 0,
  };
}

export function readMemberSession(): MemberSession {
  if (typeof window === "undefined") return guestSession;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return guestSession;
    const parsed = JSON.parse(raw) as Partial<MemberSession> & {
      idToken?: string;
      coins?: number;
      userName?: string;
    };
    const accessToken = parsed.accessToken || parsed.idToken || "";
    if (!accessToken) return guestSession;
    return {
      ...guestSession,
      ...parsed,
      isLoggedIn: true,
      id: Number(parsed.id) || 0,
      name: parsed.name || parsed.userName || "member",
      pointsBalance: Number(parsed.pointsBalance ?? parsed.coins) || 0,
      accessToken,
      accessTokenExpires: Number(parsed.accessTokenExpires) || 0,
      refreshToken: parsed.refreshToken || "",
      refreshTokenExpires: Number(parsed.refreshTokenExpires) || 0,
    };
  } catch {
    return guestSession;
  }
}

export function saveMemberSession(session: MemberSession) {
  if (typeof window === "undefined") return;
  const serialized = JSON.stringify(session);
  window.localStorage.setItem(STORAGE_KEY, serialized);
  persistNativeMemberSession(serialized);
  window.dispatchEvent(new Event("member-session-change"));
}

export function clearMemberSession() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STORAGE_KEY);
  persistNativeMemberSession(null);
  window.dispatchEvent(new Event("member-session-change"));
}

export async function ensureMemberAccessToken(): Promise<string | null> {
  const session = readMemberSession();
  if (!session.isLoggedIn) return null;
  const now = Math.floor(Date.now() / 1000);
  if (session.accessToken && session.accessTokenExpires > now + 120) {
    return session.accessToken;
  }
  if (!session.refreshToken || session.refreshTokenExpires <= now + 30) {
    clearMemberSession();
    return null;
  }
  if (!memberRefreshPromise) {
    memberRefreshPromise = (async () => {
      try {
        const refreshed = await refreshMemberToken(session.refreshToken);
        const current = readMemberSession();
        if (current.refreshToken !== session.refreshToken) {
          return current.accessToken || null;
        }
        const next: MemberSession = {
          ...session,
          accessToken: refreshed.accessToken,
          accessTokenExpires: refreshed.accessTokenExpires,
          refreshToken: refreshed.refreshToken,
          refreshTokenExpires: refreshed.refreshTokenExpires,
        };
        saveMemberSession(next);
        return next.accessToken;
      } catch (error) {
        if (isRefreshTokenRejected(error)) {
          clearMemberSession();
          return null;
        }
        throw error;
      }
    })().finally(() => {
      memberRefreshPromise = null;
    });
  }
  return memberRefreshPromise;
}

export async function refreshCurrentMember(): Promise<MemberSession> {
  const token = await ensureMemberAccessToken();
  if (!token) return guestSession;
  const current = readMemberSession();
  const profile = await fetchMemberProfile(token);
  const next = buildSessionFromProfile(profile, current);
  saveMemberSession(next);
  return next;
}

export function readReferralCode(): string {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  const fromUrl = (params.get("ref") || params.get("shareCode") || "").trim().toUpperCase();
  if (fromUrl) {
    window.localStorage.setItem(REFERRAL_KEY, fromUrl);
    return fromUrl;
  }
  return (window.localStorage.getItem(REFERRAL_KEY) || "").trim().toUpperCase();
}

export function clearReferralCode(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(REFERRAL_KEY);
}
